/**
 * Cross-platform tap feedback for nav buttons.
 * - Brief haptic buzz (when supported & not disabled in localStorage)
 * - Optional callback wrapper that ensures feedback fires *before* handler
 *
 * Visual feedback (press scale + brightness) is handled via the
 * `tap-feedback` Tailwind class string defined below — apply it to any
 * button that should feel responsive.
 */

/** Tailwind classes that give every nav button a clear "I was tapped" pulse. */
export const tapFeedbackClass =
  "transition-transform duration-75 active:scale-90 active:brightness-90 select-none";

/** Fire a short haptic pulse if the device supports it. Safe to call anywhere. */
export function triggerTapFeedback(strong = false) {
  if (typeof navigator === "undefined" || !("vibrate" in navigator)) return;
  // Honor the existing nav haptic setting — "off" silences vibrations.
  // localStorage access is wrapped: it can be undefined or THROW (Safari
  // private mode, opaque-origin jsdom, storage-restricted WebViews), and an
  // uncaught error here takes down every button that uses tap feedback.
  try {
    if (typeof window !== "undefined" && window.localStorage?.getItem("nav.hapticLevel") === "off") {
      return;
    }
  } catch {
    /* storage unavailable — default to feedback on */
  }
  try {
    navigator.vibrate(strong ? 25 : 10);
  } catch {
    /* noop */
  }
}

/** Wrap any onClick so it triggers haptic+visual feedback before running. */
export function withTapFeedback<E extends { currentTarget?: unknown }>(
  fn?: (e: E) => void | Promise<void>,
) {
  return (e: E) => {
    triggerTapFeedback();
    fn?.(e);
  };
}
